import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

import {parseInt} from 'lodash';

const frequencies = [
    {code: '-', name: 'Aucun'},
    {code: 'm', name: 'Minute'},
    {code: 'h', name: 'Heure'},
    {code: 'D', name: 'Jour'},
    {code: 'W', name: 'Semaine'},
    {code: 'M', name: 'Mois'},
    {code: 'Y', name: 'Année'},
] as const;

@Component({
    selector: 'dmp-input-cron',
    templateUrl: './input-cron.component.html',
    styleUrls: ['./input-cron.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: InputCronComponent
        }
    ],
    standalone: false
})
export class InputCronComponent implements ControlValueAccessor {
    readonly frequencies = frequencies;
    readonly minuteFrequencies = ['1', '5', '10', '15', '20', '25', '30'] as const;
    readonly minutes = ['0', '5', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'] as const;
    readonly days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'] as const;
    readonly months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'] as const;
    /** Libellés traduits ; les valeurs restent celles ci-dessus (elles servent à construire l'expression cron). */
    frequencyOptions: { label: string; value: typeof frequencies[number] }[] = [];
    dayOptions: { label: string; value: string }[] = [];
    monthOptions: { label: string; value: string }[] = [];
    readonly days_months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31'] as const;

    currentFrequency: typeof frequencies[number] = this.frequencies[0];

    constructor(private readonly translate: TranslateService) {
        const frequencyKeys = ['none', 'frequency.Mi', 'frequency.Ho', 'frequency.Da', 'frequency.We', 'frequency.Mo', 'frequency.Ye'];
        const dayKeys = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => `days.${d}`);
        const monthKeys = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => `month.${m}`);
        const prefix = (key: string) => `components.cron.${key}`;
        this.translate.stream([...frequencyKeys, ...dayKeys, ...monthKeys].map(prefix)).subscribe(t => {
            this.frequencyOptions = this.frequencies.map((f, i) => ({label: t[prefix(frequencyKeys[i])], value: f}));
            this.dayOptions = this.days.map((d, i) => ({label: t[prefix(dayKeys[i])], value: d}));
            this.monthOptions = this.months.map((m, i) => ({label: t[prefix(monthKeys[i])], value: m}));
        });
    }

    model = {
        m: '1',
        h: (Math.round(new Date().getMinutes() / 5) * 5) % 60 + '',
        D: new Date(),
        W: {
            hour: new Date(),
            day: this.days[(new Date().getDay() ?? 7) - 1]
        },
        M: {
            hour: new Date(),
            day: new Date().getDate() < 10 ? '0' + new Date().getDate() : new Date().getDate(),
        },
        Y: {
            hour: new Date(),
            day: new Date().getDate() < 10 ? '0' + new Date().getDate() : new Date().getDate(),
            month: this.months[new Date().getMonth()]
        }
    };
    @Output() cronChange = new EventEmitter();

    _cron = '';

    get cron() {
        return this._cron;
    }

    @Input() set cron(cron) {
        this.setCron(cron);
    }

    @Input() disabled = false;

    touched = false;

    private setCron(cron) {
        if (!cron) {
            this._cron = cron;
            this.currentFrequency = this.frequencies[0]; // Réinitialiser à "Aucun"
            return;
        }
        if (this._cron === cron) {
            return;
        }
        this._cron = cron;
        const model = this.getInfoFromCron(cron);
        this.model[model.code] = model.value;
        this.currentFrequency = this.frequencies.find((el) => el.code === model.code);
    }

    handleChange() {
        const code = this.currentFrequency.code;
        const model = this.model[code];
        switch (code) {
            case 'm':
                this._cron = `*/${model} * * * *`;
                break;
            case 'h':
                this._cron = `${model} * * * *`;
                break;
            case 'D':
                this._cron = `${this.getHours(model)} * * *`;
                break;
            case 'W':
                this._cron = `${this.getHours(model.hour)} * * ${this.indexToDay(this.days.indexOf(model.day))}`;
                break;
            case 'M':
                this._cron = `${this.getHours(model.hour)} ${model.day} * *`;
                break;
            case 'Y':
                this._cron = `${this.getHours(model.hour)} ${model.day} ${this.months.indexOf(model.month) + 1} *`;
                break;
            default:
                this._cron = null;
                this.onChange(this._cron);
                return;
        }
        this.onChange(this._cron);
    }

    getHours(date: Date | string) {
        if (!date) {
            date = new Date();
        }
        if (typeof date === 'string') {
            date = new Date(date);
        }
        return date.getMinutes() + ' ' + date.getHours();
    }

    indexToDay(index: number) {
        if (index === 6) {
            return 0;
        }
        return index + 1;
    }

    getInfoFromCron(cron: string) {
        let match: RegExpMatchArray;
        if ((match = cron.match(/\*\/([0-9]+) \* \* \* \*/))) {
            return {
                code: 'm',
                value: match[1],
            };
        } else if (cron === '* * * * *') {
            // Pour le cas toutes les minutes (*/1 et * sont équivalents)
            return {
                code: 'm',
                value: '1'
            };
        } else if ((match = cron.match(/([0-9]+) \* \* \* \*/))) {
            return {
                code: 'h',
                value: match[1],
            };
        } else if ((match = cron.match(/([0-9]+) ([0-9]+) \* \* \*/))) {
            return {
                code: 'D',
                value: this.setHourToNow(parseInt(match[2]), parseInt(match[1]))
            };
        } else if ((match = cron.match(/([0-9]+) ([0-9]+) \* \* ([0-9]+)/))) {
            return {
                code: 'W',
                value: {
                    hour: this.setHourToNow(parseInt(match[2]), parseInt(match[1])),
                    day: this.days[(parseInt(match[3]) ?? 7) - 1]
                }
            };
        } else if ((match = cron.match(/([0-9]+) ([0-9]+) ([0-9]+) \* \*/))) {
            return {
                code: 'M',
                value: {
                    hour: this.setHourToNow(parseInt(match[2]), parseInt(match[1])),
                    day: match[3]
                }
            };
        } else if ((match = cron.match(/([0-9]+) ([0-9]+) ([0-9]+) ([0-9]+) \*/))) {
            return {
                code: 'Y',
                value: {
                    hour: this.setHourToNow(parseInt(match[2]), parseInt(match[1])),
                    day: match[3],
                    month: this.months[parseInt(match[4]) - 1]
                }
            };
        } else {
            throw new Error(`Impossible to match cron: ${cron}.`);
        }
    }

    setHourToNow(hour: number, min: number) {
        const date = new Date();
        date.setHours(hour);
        date.setMinutes(min);
        return date;
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    writeValue(item: any): void {
        this.setCron(item);
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    onChange: any = () => { /**/
    }
    onTouched: any = () => { /**/
    }

}
